import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Search, Play, User as UserIcon, LogOut, Shield, Mail, ShoppingBag, Disc3, Trophy, ChevronDown, MessageCircle } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NotificationBell from "@/components/NotificationBell";
import MessagesBubble from "@/components/MessagesBubble";
import Tx from "@/components/Tx";

export default function Navbar({ onSearchOpen }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const { playSong } = usePlayer();
  const { user, logout } = useAuth();
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setUserMenu(false); }, [location.pathname]);

  // Touch-friendly: close user menu on outside click / escape
  useEffect(() => {
    if (!userMenu) return;
    const onDoc = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenu(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setUserMenu(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [userMenu]);

  // Lock body scroll while mobile menu is open + close on Escape
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (!open) return;
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // ====================================================================
  // CLEAN NAV STRUCTURE — 10 main sections, balanced submenus.
  // After Feb 2026 audit (Iter 79):
  //  - Home filled with 7 quick-access items (was empty)
  //  - Social slimmed from 12 → 8 (Notifications + Messages now in header only)
  //  - Wiki moved to About; Leaderboard moved to Games
  //  - Goal: every group has 3–8 well-balanced items
  // ====================================================================
  const NAV_GROUPS = [
    {
      key: "home", to: "/", label: t("nav.home", "Home"),
      children: [
        { to: "/", label: "Welcome" },
        { to: "/community", label: "Dashboard" },
        { to: "/activity", label: "What's New" },
        { to: "/news", label: "Latest News" },
        { to: "/tour-map", label: "New Tour Dates" },
        { to: "/discography", label: "New Releases" },
        { to: "/leaderboard", label: "Top Fans" },
      ],
    },
    {
      key: "news", to: "/news", label: t("nav.news", "News"),
      children: [
        { to: "/news", label: "Latest News" },
        { to: "/media-archive", label: "Media Archive" },
      ],
    },
    {
      key: "music", to: "/discography", label: t("nav.discography", "Discography"),
      children: [
        { to: "/discography", label: "Full Discography" },
        { to: "/albums", label: "Albums" },
        { to: "/singles", label: "Singles" },
        { to: "/remix-archive", label: "Remix Archive" },
        { to: "/radio", label: "Radio" },
      ],
    },
    {
      key: "tour", to: "/tour-map", label: t("nav.tour", "Tour"),
      children: [
        { to: "/tour-map", label: "Tour Atlas" },
        { to: "/archive", label: "Performance Archive (1998–2026)" },
        { to: "/timeline", label: "Timeline" },
        { to: "/events", label: "Events Calendar" },
      ],
    },
    {
      key: "media", to: "/media", label: t("nav.media", "Media"),
      children: [
        { to: "/media", label: "Media Hub" },
        { to: "/videos", label: "Videos" },
        { to: "/gallery", label: "Gallery" },
        { to: "/wallpaper-creator", label: "Wallpapers" },
        { to: "/media-archive", label: "Magazine Archive" },
      ],
    },
    {
      key: "games", to: "/games", label: t("nav.games", "Games"),
      children: [
        { to: "/games", label: "Games Hub" },
        { to: "/games/blue-world", label: "Blue World Adventure" },
        { to: "/games/penalty", label: "Penalty Challenge" },
        { to: "/games/concert-manager", label: "Concert Manager" },
        { to: "/games/character", label: "Zorotl Studio" },
        { to: "/quiz", label: "Eiffel 65 Quiz" },
        { to: "/leaderboard", label: "Leaderboard" },
      ],
    },
    {
      key: "community", to: "/community", label: t("nav.community", "Social"),
      children: [
        { to: "/community", label: "Social Hub" },
        { to: "/activity", label: "Activity Feed" },
        { to: "/forum", label: "Forum" },
        { to: "/fan-wall", label: "Fan Wall" },
        { to: "/chat", label: "Chat" },
        { to: "/groups", label: "Groups" },
        { to: "/fan-map", label: "Fan Map" },
        { to: "/polls", label: "Polls" },
      ],
    },
    {
      key: "shop", to: "/shop", label: t("nav.shop", "Shop"),
      children: [
        { to: "/shop", label: "Fan Shop Hub" },
        { to: "/marketplace", label: "Marketplace" },
        { to: "/collections", label: "Collections" },
        { to: "/fan-zone", label: "Fan Store" },
      ],
    },
    {
      key: "about", to: "/biography", label: t("nav.about", "About"),
      children: [
        { to: "/biography", label: "Biography" },
        { to: "/awards", label: "Awards" },
        { to: "/wiki", label: "Wiki" },
      ],
    },
    {
      key: "contact", to: "/contact", label: t("nav.contact", "Contact"),
      children: [
        { to: "/contact", label: "Contact Form" },
        { to: "/tickets", label: "Support Tickets" },
      ],
    },
  ];

  // Backwards-compatible flat lists (used in the expandable mobile menu fallback)
  const TOP = NAV_GROUPS.map((g) => ({ to: g.to, label: g.label }));
  void TOP; // legacy stub kept for future flat-nav fallback

  // Desktop mega-menu hover state — close after small delay so cursor can move into panel.
  const [hoverKey, setHoverKey] = useState(null);
  const hoverCloseTimer = React.useRef(null);
  const openMega = (key) => {
    if (hoverCloseTimer.current) { clearTimeout(hoverCloseTimer.current); hoverCloseTimer.current = null; }
    setHoverKey(key);
  };
  const closeMega = () => {
    if (hoverCloseTimer.current) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = setTimeout(() => setHoverKey(null), 140);
  };
  const MORE = NAV_GROUPS.flatMap((g) => g.children).filter(
    (c, i, arr) => arr.findIndex((x) => x.to === c.to) === i
  );

  const signedIn = user && user.email;

  // Unread DM badge
  const [dmUnread, setDmUnread] = useState(0);
  useEffect(() => {
    if (!signedIn) { setDmUnread(0); return; }
    let alive = true;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const fetchCount = () => axios.get(`${API}/dm/unread-count`).then((r) => { if (alive) setDmUnread(r.data?.count || 0); }).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [signedIn]);

  return (
    <header data-testid="main-navbar" className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrolled ? "py-2.5" : "py-4"}`}>
      <div className={`absolute inset-0 -z-10 pointer-events-none transition-all duration-500 ${scrolled ? "bg-[#020B1F]/90 backdrop-blur-xl border-b border-white/10 shadow-[0_4px_24px_rgba(0,0,0,0.4)]" : "bg-[#020B1F]/60 backdrop-blur-md"}`} />

      <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-[1500px] flex items-center justify-between gap-1 sm:gap-4">
        <Link to="/" data-testid="nav-logo-link" className="flex items-baseline gap-1.5 group shrink-0">
          <span className="font-display font-black text-xl sm:text-3xl tracking-tight text-white">EIFFEL</span>
          <span className="font-display font-black text-xl sm:text-3xl tracking-tight neon-text">65</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5 flex-1 justify-center">
          {NAV_GROUPS.map((g) => {
            const isOpen = hoverKey === g.key;
            const hasChildren = (g.children || []).length > 0;
            return (
              <div
                key={g.key}
                className="relative"
                onMouseEnter={() => hasChildren && openMega(g.key)}
                onMouseLeave={closeMega}
              >
                <NavLink
                  to={g.to}
                  data-testid={`nav-link-${g.key}`}
                  className={({ isActive }) =>
                    `relative px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-bold font-display transition-colors inline-flex items-center gap-1 ${
                      isActive || isOpen ? "text-[#00AFFF]" : "text-white/70 hover:text-white"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Tx>{g.label}</Tx>
                      {hasChildren && (
                        <ChevronDown
                          size={10}
                          className={`transition-transform duration-200 ${isOpen ? "rotate-180 text-[#00AFFF]" : ""}`}
                        />
                      )}
                      {(isActive || isOpen) && (
                        <span className="absolute left-2 right-2 -bottom-0.5 h-[3px] rounded-full bg-[#00AFFF] shadow-[0_0_10px_#00AFFF]" />
                      )}
                    </>
                  )}
                </NavLink>

                {hasChildren && isOpen && (
                  <div
                    data-testid={`megamenu-${g.key}`}
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-3 z-50 pointer-events-auto"
                    onMouseEnter={() => openMega(g.key)}
                    onMouseLeave={closeMega}
                  >
                    {/* Floating panel */}
                    <div
                      className="rounded-2xl border border-white/10 overflow-hidden min-w-[260px] max-w-[320px] shadow-[0_24px_60px_rgba(0,0,0,0.55)]"
                      style={{
                        background: "linear-gradient(180deg, rgba(10,20,40,0.96) 0%, rgba(2,11,31,0.98) 100%)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        animation: "megamenuFadeIn 0.18s ease-out",
                      }}
                    >
                      <div className="px-3 py-2 border-b border-white/8 text-[9px] uppercase tracking-[0.3em] text-[#00AFFF]/80 font-display font-bold">
                        <Tx>{g.label}</Tx>
                      </div>
                      <ul className="py-1.5">
                        {g.children.map((c) => (
                          <li key={c.to}>
                            <NavLink
                              to={c.to}
                              data-testid={`megamenu-${g.key}-item-${c.to.replace(/[^a-z0-9]/gi, "-")}`}
                              onClick={() => setHoverKey(null)}
                              className={({ isActive }) =>
                                `block px-3.5 py-2 mx-1.5 rounded-lg text-[12px] font-display font-bold transition-all duration-150 ${
                                  isActive
                                    ? "bg-[#00AFFF]/12 text-[#00AFFF]"
                                    : "text-white/75 hover:bg-white/5 hover:text-[#00AFFF] hover:translate-x-0.5"
                                }`
                              }
                            >
                              <Tx>{c.label}</Tx>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
          <button data-testid="nav-search-btn" onClick={onSearchOpen} aria-label="Search" className="w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded-full text-white/70 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10 transition-all">
            <Search size={17} />
          </button>

          <div className="hidden sm:block"><LanguageSwitcher /></div>
          {/* Messages: desktop bubble + mobile icon link */}
          {signedIn && <div className="hidden md:block"><MessagesBubble /></div>}
          {signedIn && (
            <NavLink
              to="/messages"
              data-testid="nav-messages-icon"
              className="relative w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded-full text-white/70 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10 transition-all md:hidden"
              aria-label="Messages"
            >
              <Mail size={17} />
              {dmUnread > 0 && (
                <span data-testid="nav-messages-icon-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-[#FF3B6B] text-white text-[9px] font-display font-black">
                  {dmUnread > 9 ? "9+" : dmUnread}
                </span>
              )}
            </NavLink>
          )}
          {/* Chat icon hidden on <sm (reachable via Community menu group) — bell stays visible */}
          {signedIn && (
            <>
              <NavLink
                to="/chat"
                data-testid="nav-chat-icon"
                className="w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded-full text-white/70 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10 transition-all hidden sm:grid md:hidden"
                aria-label="Chat"
              >
                <MessageCircle size={17} />
              </NavLink>
              <NotificationBell />
            </>
          )}
          {/* Desktop bell stays visible regardless to show empty state */}
          {!signedIn && <div className="hidden md:block"><NotificationBell /></div>}

          {signedIn ? (
            <div className="relative" ref={userMenuRef}>
              <button data-testid="nav-user-btn" onClick={() => setUserMenu((v) => !v)} className="relative inline-flex items-center gap-2 px-2 sm:px-2.5 py-1.5 rounded-full border border-[#00AFFF]/40 text-[#00AFFF] hover:bg-[#00AFFF]/10 transition min-h-[40px]">
                {user.avatar ? <img src={user.avatar} alt="" className="w-6 h-6 rounded-full object-cover" /> : <span className="w-6 h-6 grid place-items-center rounded-full bg-[#00AFFF]/15 text-[10px] font-display font-black">{user.username?.[0]?.toUpperCase()}</span>}
                <span className="hidden sm:inline text-[11px] font-display font-bold uppercase tracking-wider max-w-[80px] truncate">{user.username}</span>
              </button>
              {userMenu && (
                <div data-testid="nav-user-menu" className="absolute right-0 top-full mt-2 glass-strong rounded-xl p-2 border border-[#00AFFF]/30 shadow-2xl w-56">
                  <Link to="/profile" data-testid="nav-user-menu-profile" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-white/85 hover:bg-white/5 hover:text-[#00AFFF]">
                    <UserIcon size={13}/> {t("nav.profile")}
                  </Link>
                  <Link to="/messages" data-testid="nav-user-menu-messages" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-white/85 hover:bg-white/5 hover:text-[#00AFFF]">
                    <Mail size={13}/> {t("messages.title", "Nachrichten")}
                    {dmUnread > 0 && (
                      <span data-testid="nav-dm-unread" className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-[#FF3B6B] text-white text-[10px] font-display font-black">
                        {dmUnread > 9 ? "9+" : dmUnread}
                      </span>
                    )}
                  </Link>
                  <Link to="/marketplace" data-testid="nav-user-menu-marketplace" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-white/85 hover:bg-white/5 hover:text-[#00AFFF]">
                    <ShoppingBag size={13}/> {t("market.title", "Marktplatz")}
                  </Link>
                  <Link to="/my-collection" data-testid="nav-user-menu-my-collection" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-white/85 hover:bg-white/5 hover:text-[#00AFFF]">
                    <Disc3 size={13}/> My Collection
                  </Link>
                  <Link to="/leaderboard" data-testid="nav-user-menu-leaderboard" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-white/85 hover:bg-white/5 hover:text-[#00AFFF]">
                    <Trophy size={13}/> Leaderboard
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin" data-testid="nav-user-menu-admin" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-[#FF3B6B] hover:bg-white/5">
                      <Shield size={13}/> {t("nav.admin")}
                    </Link>
                  )}
                  <button data-testid="nav-user-menu-logout" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-white/75 hover:bg-white/5 hover:text-red-400 text-left">
                    <LogOut size={13}/> {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login-btn" className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:text-[#00AFFF] hover:border-[#00AFFF]/50 transition-all text-[10px] sm:text-[11px] font-display font-bold uppercase tracking-wider min-h-[40px]">
              <UserIcon size={12} /> <span className="hidden xs:inline sm:inline">{t("nav.login")}</span>
            </Link>
          )}

          <button data-testid="nav-listen-now-btn" onClick={() => playSong("blue")} className="hidden md:inline-flex items-center gap-2 bg-[#00AFFF] hover:bg-[#4DC3FF] text-[#020B1F] px-3 sm:px-4 py-2 rounded-full font-display font-black text-[11px] uppercase tracking-[0.18em] shadow-[0_0_20px_rgba(0,175,255,0.5)] hover:shadow-[0_0_30px_rgba(0,175,255,0.8)] hover:-translate-y-0.5 transition-all">
            <Tx>Listen</Tx> <Play size={11} fill="currentColor" />
          </button>

          <button data-testid="nav-menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu" className="xl:hidden w-10 h-10 sm:w-11 sm:h-11 grid place-items-center rounded-full text-white hover:bg-[#00AFFF]/10 hover:text-[#00AFFF] transition-all active:scale-95">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <button data-testid="nav-more-toggle" onClick={() => setOpen((v) => !v)} aria-label="More menu" className="hidden xl:grid w-10 h-10 place-items-center rounded-full text-white/70 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10 transition-all">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <div className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${open ? "max-h-[calc(100vh-64px)] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1500px] pt-4 sm:pt-6 pb-8 overflow-y-auto max-h-[calc(100vh-80px)]">
          <div data-testid="menu-grouped" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
            {NAV_GROUPS.map((g, idx) => (
              <div key={g.key} data-testid={`menu-group-${g.key}`} className="space-y-1 glass rounded-2xl p-3 sm:p-2 border border-white/5 sm:border-transparent sm:bg-transparent sm:shadow-none">
                <NavLink
                  to={g.to}
                  data-testid={`menu-group-link-${g.key}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `block px-3 py-3 sm:py-2 rounded-lg min-h-[56px] sm:min-h-0 ${
                    isActive ? "bg-[#00AFFF]/15 text-[#00AFFF]" : "text-white hover:bg-white/5 active:bg-white/10"
                  }`}
                >
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-white/45 mb-0.5">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display font-black text-base sm:text-sm tracking-wide">
                    <Tx>{g.label}</Tx>
                  </div>
                </NavLink>
                {g.children.length > 0 ? (
                  <ul className="pl-3 space-y-0.5 sm:space-y-0.5">
                    {g.children.map((c) => (
                      <li key={c.to}>
                        <NavLink
                          to={c.to}
                          onClick={() => setOpen(false)}
                          data-testid={`menu-sublink-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={({ isActive }) => `block px-3 py-2.5 sm:py-1.5 rounded-md text-[13px] sm:text-[12px] min-h-[40px] sm:min-h-0 transition active:bg-white/5 ${
                            isActive ? "text-[#00AFFF] bg-[#00AFFF]/5 sm:bg-transparent" : "text-white/70 hover:text-[#00AFFF]"
                          }`}
                        >
                          <Tx>{c.label}</Tx>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
          {!signedIn && (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="menu-mobile-login"
                className="inline-flex items-center gap-1.5 px-5 py-3 sm:py-2 rounded-full bg-[#00AFFF] text-[#020B1F] font-display font-bold uppercase tracking-widest text-[12px] min-h-[48px] sm:min-h-0"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/register"
                onClick={() => setOpen(false)}
                data-testid="menu-mobile-register"
                className="inline-flex items-center gap-1.5 px-5 py-3 sm:py-2 rounded-full border border-white/20 text-white/85 font-display font-bold uppercase tracking-widest text-[12px] min-h-[48px] sm:min-h-0"
              >
                {t("nav.register", "Register")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
